/**
 * SAISEN — Cart drawer: pressing "-" at quantity 1 removes the line item.
 *
 * Behaviour:
 * - Quantity > minimum: "-" decrements normally (native theme behaviour,
 *   untouched).
 * - Quantity <= minimum (normally 1): "-" removes the line item instead,
 *   reusing the exact same standard Shopify endpoint (/cart/change.js with
 *   quantity: 0) and the theme's own cart-drawer custom element
 *   (renderContents) that the trash/remove icon already uses — no separate
 *   removal mechanism, no custom cart UI reinvented.
 * - "+" and the trash icon keep their current, untouched behaviour.
 *
 * Why this needs to be more aggressive than "watch the disabled attribute":
 * inspection of the theme's own CSS shows the "stuck at minimum" look is
 * driven by MULTIPLE possible mechanisms at once —
 *   .quantity__button.disabled { cursor: not-allowed; opacity: .5 }
 * (a CLASS, not just the disabled attribute) — and a real HTML "disabled"
 * button never dispatches click/keyboard-activation events at all, no
 * matter what JS or CSS says. So this script clears every mechanism it can
 * find (disabled attribute, "disabled" class, aria-disabled, tabindex=-1)
 * every time any of them changes AND on a short safety interval, so the
 * button can never stay stuck even if the vendor code re-applies one of
 * them after a render.
 */
(function () {
  var MINUS_SELECTOR = '.quantity__button--minus';
  var POLL_INTERVAL_MS = 150;

  function getCartDrawer() {
    return document.querySelector('cart-drawer');
  }

  function clearDisabledLook(btn) {
    if (btn.hasAttribute('disabled')) btn.removeAttribute('disabled');
    if (btn.classList.contains('disabled')) btn.classList.remove('disabled');
    if (btn.getAttribute('aria-disabled') === 'true') btn.removeAttribute('aria-disabled');
    if (btn.getAttribute('tabindex') === '-1') btn.removeAttribute('tabindex');
  }

  function enableStuckMinusButtons(root) {
    root.querySelectorAll(MINUS_SELECTOR).forEach((btn) => {
      const wrapper = btn.closest('quantity-input');
      const input = wrapper && wrapper.querySelector('.quantity__input');
      if (!input) return;

      const min = parseInt(input.min, 10) || 1;
      const current = parseInt(input.value, 10) || 0;
      // Only ever force-enable at/under minimum: above minimum, leave the
      // native enabled/disabled bookkeeping (e.g. stock limits) untouched.
      if (current <= min) clearDisabledLook(btn);
    });
  }

  async function removeLine(line, drawerRoot) {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        line: parseInt(line, 10),
        quantity: 0,
        sections: 'cart-drawer,cart-icon-bubble',
        sections_url: window.location.pathname,
      }),
    });

    const data = await response.json();

    if (drawerRoot && typeof drawerRoot.renderContents === 'function' && data.sections) {
      drawerRoot.renderContents(data);
      // renderContents() does not reliably toggle "is-empty" on <cart-drawer>
      // itself (same workaround already present natively in
      // layout/theme.liquid for the cart discount-code flow). Removing the
      // last item must re-add "is-empty" so the theme's own empty-cart
      // message shows correctly instead of a stale, wrongly-sized body.
      drawerRoot.classList.toggle('is-empty', data.item_count === 0);
    } else {
      window.location.reload();
    }
  }

  function handleActivate(btn, drawerRoot) {
    const wrapper = btn.closest('quantity-input');
    const input = wrapper && wrapper.querySelector('.quantity__input');
    if (!input) return false;

    const min = parseInt(input.min, 10) || 1;
    const current = parseInt(input.value, 10) || 0;
    if (current > min) return false; // above minimum: native decrement handles it

    if (btn.dataset.saisenRemoving === 'true') return true; // already in flight
    const line = input.dataset.index;
    if (!line) return false;

    btn.dataset.saisenRemoving = 'true';
    clearDisabledLook(btn);
    removeLine(line, drawerRoot)
      .catch((error) => {
        console.error('SAISEN cart: failed to remove item at minimum quantity.', error);
        window.location.reload();
      })
      .finally(() => {
        delete btn.dataset.saisenRemoving;
      });
    return true;
  }

  function init(drawerRoot) {
    enableStuckMinusButtons(drawerRoot);

    // Mouse / touch clicks, captured before the vendor widget's own handler
    // so we can decide "remove" vs "let native decrement happen" first.
    drawerRoot.addEventListener(
      'click',
      (event) => {
        const btn = event.target.closest(MINUS_SELECTOR);
        if (!btn) return;
        if (handleActivate(btn, drawerRoot)) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      },
      true
    );

    // Keyboard activation (Enter / Space) on a focused "-" button: browsers
    // normally turn this into a synthetic click automatically, but we also
    // handle it explicitly here in case the button was only just re-enabled
    // (e.g. right after a render) and hasn't received a native click yet.
    drawerRoot.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
        const btn = event.target.closest && event.target.closest(MINUS_SELECTOR);
        if (!btn) return;
        if (handleActivate(btn, drawerRoot)) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      },
      true
    );

    // Catch every way the vendor widget might re-apply a disabled look
    // (attribute, class, aria-disabled) on any render or state change.
    new MutationObserver(() => enableStuckMinusButtons(drawerRoot)).observe(drawerRoot, {
      attributes: true,
      attributeFilter: ['disabled', 'class', 'aria-disabled', 'value'],
      subtree: true,
      childList: true,
    });

    // Safety net: some widgets re-apply their disabled state slightly after
    // the triggering DOM mutation (e.g. on their own debounce/tick), which
    // could momentarily slip past the observer. A cheap, short poll makes
    // sure the button never stays stuck for more than ~150ms.
    // Perf : ce filet de securite ne sert que lorsque le tiroir est ouvert
    // (boutons visibles). Tiroir ferme, on ne parcourt plus le panier.
    setInterval(() => {
      if (drawerRoot.classList.contains('active')) enableStuckMinusButtons(drawerRoot);
    }, POLL_INTERVAL_MS);
  }

  function start() {
    const drawerRoot = getCartDrawer();
    if (drawerRoot) init(drawerRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
